import Vue from "vue";

import lodashGet from "lodash/get";
import lodashHas from "lodash/has";
import lodashUniqBy from "lodash/uniqBy";

import store from "@/store";
import { GET_GROUP_MEMBERS } from "@/store/actions.type";
import { MODULE_PREFIX_USER } from "@/store";
import { encodeAllURLParameters } from "@/common/api.utilities";
import {
  ENDPOINT_COMMUNICATION,
  ENDPOINT_ASSETS,
} from "@/common/constants/api";
import {
  httpGet,
  httpPost,
  httpPatch,
  httpDelete,
} from "@/services/api.service";

import MASS_COMM_CONSTANTS from "../../common/constants/massComm/massComm.js";

export { _getGroups as getGroups };
export { _getGroup as getGroup };
export { _saveGroup as saveGroup };
export { _copyGroup as copyGroup };
export { _deleteGroup as deleteGroup };
export { _getGroupUsers as getGroupUsers };
export { _saveUserToGroup as saveUserToGroup };
export { _deleteUserFromGroup as deleteUserFromGroup };
export { GROUP_MEMBER_ICON as groupMemberIcon };
export { _loadGroupMemberIcon as loadGroupMemberIcon };
export { _groupMemberLocationsToFeatures as groupMemberLocationsToFeatures };
export { _getGroupMembersLocation as getGroupMembersLocation };
export { _getGeofencedMembersLocations as getGeofencedMembersLocations };

export { _getMessages as getMessages };
export { _getMessage as getMessage };
export { _saveMessage as saveMessage };
export { _deleteMessage as deleteMessage };

export { _getLocations as getLocations };
export { _getLocationById as getLocationById };
export { _editLocation as editLocation };
export { _addLocation as addLocation };
export { _deleteLocation as deleteLocation };

export { _getMessageAcknowledgements as getMessageAcknowledgements };
export { _acknowledgementRecieved as acknowledgementRecieved };
export { _acknowledgementViewed as acknowledgementViewed };
export { _acknowledgementReplied as acknowledgementReplied };

export { _getMemberDetails as getMemberDetails };

/// MESSAGES

const _getMessages = async (params) => {
  try {
    params.draft = params.draft ? params.draft : false;
    const RESULT = await httpGet({
      url: `${ENDPOINT_COMMUNICATION}/messages?draft=${params.draft}`,
      noCache: true,
    });
    const { data } = JSON.parse(RESULT);

    if (data) {
      return data;
    }
    return false;
  } catch (error) {
    console.error("Unable to get messages: ", JSON.stringify(error));
    return false;
  }
};

const _getMessage = async (params) => {
  try {
    if (!params || !params.messageId) {
      console.error("Unable to get message. No messageId.");
      return false;
    }
    const RESULT = await httpGet({
      url: `${ENDPOINT_COMMUNICATION}/messages/${params.messageId}`,
      noCache: params.noCache,
    });
    const { data } = JSON.parse(RESULT);

    if (data) {
      return data;
    }
    return false;
  } catch (error) {
    console.error(
      `Unable to get message "${params.messageId}": `,
      JSON.stringify(error)
    );
    return false;
  }
};

const _saveMessage = async (params) => {
  if (!params || !params.message) {
    console.error("Unable to save message");
    return false;
  }
  if (params.message.messageId) {
    return await _editMessage(params);
  }
  return await _addMessage(params);
};

const _addMessage = async (params) => {
  try {
    if (
      !params ||
      !params.message ||
      params.message.messageId ||
      !params.message.subject ||
      ((!params.message.groups || !params.message.groups.length) &&
        (!params.message.locationIds || !params.message.locationIds.length))
    ) {
      console.error("Unable to add message");
      return false;
    }

    let postRes = await httpPost({
      url: `${ENDPOINT_COMMUNICATION}/messages`,
      data: JSON.parse(JSON.stringify(params.message)),
    });
    postRes = JSON.parse(postRes);

    if (postRes && postRes.data && postRes.data.messageId) {
      return postRes.data.messageId;
    }

    return true;
  } catch (error) {
    console.error("Unable to add message: ", JSON.stringify(error));
    if (lodashHas(error, "data.message")) {
      throw new Error(lodashGet(error, "data.message"));
    }
    return false;
  }
};

const _editMessage = async (params) => {
  try {
    if (
      !params ||
      !params.message ||
      !lodashGet(params, "message.messageId") ||
      !params.message.subject ||
      //TODO message.groups probably needs to be groupIds right?
      ((!params.message.groups || !params.message.groups.length) &&
        (!params.message.locationIds || !params.message.locationIds.length))
    ) {
      console.error("Unable to edit message");
      return false;
    }
    await httpPatch({
      url: `${ENDPOINT_COMMUNICATION}/messages/${params.message.messageId}`,
      data: JSON.parse(JSON.stringify(params.message)),
    });

    return true;
  } catch (error) {
    console.error("Unable to edit message: ", JSON.stringify(error));
    if (lodashHas(error, "data.message")) {
      throw new Error(lodashGet(error, "data.message"));
    }
    return false;
  }
};

const _deleteMessage = async (params) => {
  try {
    if (!lodashGet(params, "message.messageId")) {
      console.error("Unable to delete message. No message id.");
      return false;
    }
    await httpDelete({
      url: `${ENDPOINT_COMMUNICATION}/${params.message.messageId}`,
      noCache: true,
    });

    return true;
  } catch (error) {
    console.error(
      `Unable to delete message "${params.message.id}": `,
      JSON.stringify(error)
    );
    return false;
  }
};

/// GROUPS

const _getGroups = async (params) => {
  try {
    const RESULT = await httpGet({
      url: `${ENDPOINT_COMMUNICATION}/groups`,
      noCache: true,
    });
    const { data } = JSON.parse(RESULT);

    if (data) {
      return data;
    }
    return false;
  } catch (error) {
    console.error("Unable to get groups: ", JSON.stringify(error));
    return false;
  }
};

const _getGroup = async (params) => {
  try {
    if (!lodashGet(params, "groupId")) {
      console.error("Unable to get group. No groupId.");
      return false;
    }
    const RESULT = await httpGet({
      url: `${ENDPOINT_COMMUNICATION}/groups/${params.groupId}`,
      noCache: true,
    });
    const { data } = JSON.parse(RESULT);

    if (data) {
      return data;
    }
    return false;
  } catch (error) {
    console.error(
      `Unable to get group "${params.groupId}": `,
      JSON.stringify(error)
    );
    return false;
  }
};

const _saveGroup = async (params) => {
  if (!params || !params.group) {
    console.error("Unable to save group");
    return false;
  }
  if (params.group.groupId) {
    return await _editGroup(params);
  }
  return await _addGroup(params);
};

const _addGroup = async (params) => {
  try {
    if (
      !params ||
      !params.group ||
      params.group.groupId ||
      !params.group.name
    ) {
      console.error("Unable to add group");
      return false;
    }

    let postRes = await httpPost({
      url: `${ENDPOINT_COMMUNICATION}/groups`,
      data: JSON.parse(JSON.stringify(params.group)),
    });
    postRes = JSON.parse(postRes);

    if (postRes.data && postRes.data.groupId) {
      return postRes.data.groupId;
    }

    return true;
  } catch (error) {
    console.error("Unable to add group: ", JSON.stringify(error));
    if (lodashHas(error, "data.message")) {
      throw new Error(lodashGet(error, "data.message"));
    }
    return false;
  }
};

const _copyGroup = async (params) => {
  try {
    if (!params || !params.name || !params.groupId) {
      console.error("Unable to copy group");
      return false;
    }

    let postRes = await httpPost({
      url: `${ENDPOINT_COMMUNICATION}/groups/${params.groupId}/copy`,
      data: { name: params.name },
    });
    postRes = JSON.parse(postRes);

    if (postRes.data && postRes.data.groupId) {
      return postRes.data.groupId;
    }

    return true;
  } catch (error) {
    console.error("Unable to add group: ", JSON.stringify(error));
    if (lodashHas(error, "data.message")) {
      throw new Error(lodashGet(error, "data.message"));
    }
    return false;
  }
};

const _editGroup = async (params) => {
  try {
    if (
      !params ||
      !params.group ||
      !params.group.groupId ||
      !params.group.name
    ) {
      console.error("Unable to edit group");
      return false;
    }
    await httpPatch({
      url: `${ENDPOINT_COMMUNICATION}/groups/${params.group.groupId}`,
      data: JSON.parse(JSON.stringify(params.group)),
      giveFailedResponse: true,
    });

    return true;
  } catch (error) {
    console.error("Unable to edit group: ", JSON.stringify(error));
    if (lodashHas(error, "data.message")) {
      throw new Error(lodashGet(error, "data.message"));
    }
    return false;
  }
};

const _deleteGroup = async (params) => {
  try {
    if (!lodashGet(params, "group.groupId")) {
      console.error("Unable to delete group. No group id.");
      return false;
    }
    await httpDelete({
      url: `${ENDPOINT_COMMUNICATION}/${params.group.groupId}`,
      noCache: true,
    });

    return true;
  } catch (error) {
    console.error(
      `Unable to delete group "${params.group.groupId}": `,
      JSON.stringify(error)
    );
    return false;
  }
};

/// GROUP USERS

const GROUP_MEMBER_ICON = "mass_comm_group_member_generic";

const _getGroupUsers = async (params) => {
  try {
    if (lodashGet(params, "groupId")) {
      const RESULT = await httpGet({
        url: `${ENDPOINT_COMMUNICATION}/groups/${params.groupId}/members`,
        noCache: true,
      });
      const { data } = JSON.parse(RESULT);

      await store.dispatch(MODULE_PREFIX_USER + GET_GROUP_MEMBERS);
      const auth0GroupUsers =
        store.getters[MODULE_PREFIX_USER + "groupMembers"];

      let groupUsers = [];
      data.forEach((user) => {
        const len = auth0GroupUsers.length;
        for (let i = 0; i < len; i++) {
          const authUser = auth0GroupUsers[i];
          if (authUser.id === user.id) {
            Vue.set(groupUsers, groupUsers.length, authUser);
            break;
          }
        }
      });

      groupUsers = lodashUniqBy(groupUsers, "id");

      return groupUsers;
    }
    return false;
  } catch (error) {
    console.error("Unable to get group users: ", JSON.stringify(error));
    return false;
  }
};

const _saveUserToGroup = async (params) => {
  try {
    if (!lodashGet(params, "groupId") || !lodashGet(params, "userId")) {
      console.error("Unable to save user to group");
      return false;
    }

    await httpPost({
      url: `${ENDPOINT_COMMUNICATION}/groups/${
        params.groupId
      }/members/${encodeURI(params.userId)}`,
    });

    return true;
  } catch (error) {
    console.error("Unable to add user to group: ", JSON.stringify(error));
    if (lodashHas(error, "data.message")) {
      throw new Error(lodashGet(error, "data.message"));
    }
    return false;
  }
};

const _deleteUserFromGroup = async (params) => {
  try {
    if (!lodashGet(params, "groupId") || !lodashGet(params, "userId")) {
      console.error("Unable to delete user from group");
      return false;
    }
    await httpDelete({
      url: `${ENDPOINT_COMMUNICATION}/groups/${
        params.groupId
      }/members/${encodeURI(params.userId)}`,
      noCache: true,
    });

    return true;
  } catch (error) {
    console.error(
      "Unable to delete article from folder:",
      JSON.stringify(error)
    );
    return false;
  }
};

const _loadMapIcon = (map, id, src) => {
  let img = new Image();
  img.onload = () => {
    map.addImage(id, img);
  };
  img.src = src;
};

const _loadGroupMemberIcon = (map) => {
  _loadMapIcon(map, GROUP_MEMBER_ICON, MASS_COMM_CONSTANTS.Icons.Map);
};

const _getGroupMembersLocation = async (group) => {
  try {
    let memberLocations = [];
    const RESULT = await httpGet({
      url: `${ENDPOINT_ASSETS}/locations?group_id=${group.groupId}`,
      noCache: true,
    });
    const { data } = JSON.parse(RESULT);
    if (data && data.assets && data.assets.features) {
      memberLocations = data.assets.features;
      return memberLocations;
    }
    return false;
  } catch (error) {
    console.error(
      "Unable to get geolocations for members: ",
      JSON.stringify(error)
    );
    return false;
  }
};

const _groupMemberLocationsToFeatures = (groupMemberLocations) => {
  let res = [];
  if (groupMemberLocations) {
    for (let i = 0; i < groupMemberLocations.length; i++) {
      let cur = groupMemberLocations[i];
      cur.icon = GROUP_MEMBER_ICON;
      res.push({
        type: "Feature",
        properties: cur.properties,
        geometry: {
          type: "Point",
          coordinates: cur.geometry.coordinates,
        },
      });
    }
  }
  return res;
};

const _getGeofencedMembersLocations = async (geofence) => {
  try {
    let memberLocations = [];
    const RESULT = await httpGet({
      url: `${ENDPOINT_ASSETS}/locations?geo_id=${geofence.id}`,
      noCache: true,
    });
    const { data } = JSON.parse(RESULT);
    if (data && data.assets && data.assets.features) {
      memberLocations = data.assets.features;

      let geofenceGeoJson = {
        type: "Feature",
        properties: {
          id: data.geoFence.properties.id,
        },
        geometry: {
          type: "Polygon",
          coordinates: [data.geoFence.geometry.coordinates],
        },
      };
      return {
        groupMembersLocations: memberLocations,
        geofence: geofenceGeoJson,
      };
    }
    return false;
  } catch (error) {
    console.error(
      "Unable to get geolocations for members: ",
      JSON.stringify(error)
    );
    return false;
  }
};

// LOCATIONS

const _getLocations = async (params) => {
  try {
    const RESULT = await httpGet({
      url: `${ENDPOINT_COMMUNICATION}/locations`,
      noCache: true,
    });
    const { data } = JSON.parse(RESULT);
    if (data && data.data) {
      return data.data;
    }
    return false;
  } catch (error) {
    console.error("Unable to get locations: ", JSON.stringify(error));
    return false;
  }
};

const _getLocationById = async (locationId) => {
  try {
    if (!locationId) {
      console.error("Unable to get location");
      return false;
    }
    let getRes = await httpGet({
      url: `${ENDPOINT_COMMUNICATION}/locations/${locationId}`,
    });
    getRes = JSON.parse(getRes);
    if (lodashHas(getRes, "data.location")) {
      return getRes.data.location;
    }
    return true;
  } catch (error) {
    console.error("Unable to get location: ", JSON.stringify(error));
    if (lodashHas(error, "data.message")) {
      throw new Error(lodashGet(error, "data.message"));
    }
    return false;
  }
};

const _addLocation = async (params) => {
  try {
    if (!params || !params.location || !params.name) {
      console.error("Unable to add location");
      return false;
    }
    let postRes = await httpPost({
      url: `${ENDPOINT_COMMUNICATION}/locations`,
      data: JSON.parse(JSON.stringify(params)),
      giveFailedResponse: true,
    });
    postRes = JSON.parse(postRes);
    if (lodashHas(postRes, "data.location")) {
      return postRes.data.location;
    }
    return true;
  } catch (error) {
    if (error && error.data && error.data.message) {
      throw error.data;
    } else {
      throw error;
    }
  }
};

const _editLocation = async (params) => {
  try {
    if (!params || !params.location || !params.id || !params.name) {
      console.error("Unable to edit location");
      return false;
    }

    await httpPatch({
      url: `${ENDPOINT_COMMUNICATION}/locations/${params.id}`,
      data: JSON.parse(JSON.stringify(params)),
      giveFailedResponse: true,
    });
    return true;
  } catch (error) {
    if (error && error.data && error.data.message) {
      throw error.data;
    } else {
      throw error;
    }
  }
};

const _deleteLocation = async (locationId) => {
  try {
    if (!locationId) {
      console.error("Unable to delete location");
      return false;
    }
    let deleteRes = await httpDelete({
      url: `${ENDPOINT_COMMUNICATION}/locations/${locationId}`,
    });
    deleteRes = JSON.parse(deleteRes);
    if (lodashHas(deleteRes, "data.location")) {
      return deleteRes.data.location;
    }
    return true;
  } catch (error) {
    console.error("Unable to delete location: ", JSON.stringify(error));
    if (lodashHas(error, "data.message")) {
      throw new Error(lodashGet(error, "data.message"));
    }
    return false;
  }
};

/// ACKNOWLEDGEMENTS

const _getMessageAcknowledgements = async (params) => {
  try {
    if (!params || !params.messageId) {
      console.error("Unable to get message acknowledgements. No messageId.");
      return false;
    }
    const RESULT = await httpGet({
      url: `${ENDPOINT_COMMUNICATION}/messages/${params.messageId}/acknowledgements`,
      noCache: params.noCache,
    });
    const { data } = JSON.parse(RESULT);

    if (data) {
      return data;
    }
    return false;
  } catch (error) {
    console.error(
      `Unable to get message acknowledgements "${params.messageId}": `,
      JSON.stringify(error)
    );
    return false;
  }
};

const _acknowledgementRecieved = (acknowledgement) => {
  return !!acknowledgement;
};

const _acknowledgementViewed = (acknowledgement) => {
  return (
    acknowledgement &&
    acknowledgement.status &&
    (acknowledgement.status === "viewed" ||
      acknowledgement.status === "responded")
  );
};

const _acknowledgementReplied = (acknowledgement) => {
  return (
    acknowledgement &&
    acknowledgement.status &&
    acknowledgement.status === "responded"
  );
};

/// MEMBERS

const _getMemberDetails = async (params) => {
  try {
    if (!params || !params.assets || !params.assets.length) {
      console.error("Unable to get member details. No assets.");
      return false;
    }
    let result = await httpPost({
      url: `${ENDPOINT_ASSETS}/locations`,
      data: JSON.parse(JSON.stringify(params)),
      giveFailedResponse: true,
    });
    result = JSON.parse(result);
    if (lodashHas(result, "data")) {
      return result.data;
    }
    return false;
  } catch (error) {
    console.error(
      `Unable to get member details for member "${params.memberId}": `,
      JSON.stringify(error)
    );
    return false;
  }
};
